import React, { useCallback } from "react";

import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import {
  ScrollContextProvider,
  scrollTo,
  ScrollToDestination,
} from "@app/hooks/useScrollToTop";
import { Drawer, styled, useMediaQuery, useTheme } from "@mui/material";
import { MainPageSideBar } from "./MainPageSideBar";
import { useAppSelector } from "@app/hooks/useAppDispatch";
import { closeDrawer, selectDrawerIsOpened } from "../redux/drawerSlice";
import { useDispatch } from "react-redux";
import { AccessRouteControl } from "@app/components/AccessRouteControl";
import { HomePage } from "@app/pages/home/HomePage";
import { rootPages } from "@app/const/route.const";
import { CarModuleSectionCode } from "@app/hooks/useUserAccess";
import { SettingsPage } from "@app/pages/settings/SettingsPage";
import { SimulationPage } from "@app/pages/simulation/SimulationPage";
import { AssetClassAnalysisPage } from "@app/pages/assetClassAnalysis/AssetClassAnalysisPage";
import { PortfolioAnalysisPage } from "@app/pages/portfolioAnalysis/PortfolioAnalysisPage";
import { AssetAllocationPage } from "@app/pages/assetAllocation/AssetAllocationPage";
import { FinancialPlanningPage } from "@app/pages/financialPlanning/FinancialPlanningPage";
import { DebugPage } from "@app/pages/debug/DebugPage";
import { DebugIconsPage } from "@app/pages/debug/DebugIconsPage";
import { isDebug } from "@app/const";
import { NotificationsPage } from "@app/pages/notifications/NotificationsPage";
import { DebugPdfPage } from "@app/pages/debug/DebugPdfPage";

const SimpleBarStyled = styled(SimpleBar)({
  height: "100%",
  width: "100%",
  ".main-page-layout-content": {
    minHeight: "100%",
    display: "flex",
  },
});

export const MainPage = () => {
  const drawerOpen = useAppSelector(selectDrawerIsOpened);

  const theme = useTheme();
  const isSideBarVisible = useMediaQuery(theme.breakpoints.up("xl"));

  const scrollableNodeRef = React.createRef<HTMLDivElement>();

  const dispatch = useDispatch();

  const handleCloseDrawer = () => dispatch(closeDrawer());

  const handleScroll = useCallback(
    (destination: ScrollToDestination) => {
      setTimeout(() => {
        if (scrollableNodeRef.current) {
          scrollTo(destination, scrollableNodeRef.current);
        }
      }, 100);
    },
    [scrollableNodeRef],
  );

  return (
    <>
      {isSideBarVisible ? (
        <MainPageSideBar />
      ) : (
        <Drawer anchor="left" open={drawerOpen} onClose={handleCloseDrawer}>
          <MainPageSideBar afterClick={handleCloseDrawer} />
        </Drawer>
      )}
      <SimpleBarStyled
        classNames={{ contentEl: "main-page-layout-content" }}
        scrollableNodeProps={{ ref: scrollableNodeRef }}
      >
        <ScrollContextProvider value={handleScroll}>
          <AccessRouteControl
            items={[
              {
                path: rootPages.home,
                element: <HomePage />,
                index: true,
                moduleSection: CarModuleSectionCode.CMPAS_HOME,
              },
              {
                path: rootPages.simulationPath,
                element: <SimulationPage />,
                moduleSection: CarModuleSectionCode.SIMULATION,
              },
              {
                path: rootPages.assetClassAnalysisPath,
                element: <AssetClassAnalysisPage />,
                moduleSection: CarModuleSectionCode.ASSET_CLASS_ANALYSIS,
              },
              {
                path: rootPages.portfolioAnalysisPath,
                element: <PortfolioAnalysisPage />,
                // moduleSection: CarModuleSectionCode.PORTFOLIO_ANALYSIS,
              },
              {
                path: rootPages.assetAllocationPath,
                element: <AssetAllocationPage />,
                moduleSection: CarModuleSectionCode.ASSET_ALLOCATION,
              },
              {
                path: rootPages.financialPlanningPath,
                element: <FinancialPlanningPage />,
                moduleSection: CarModuleSectionCode.PLANNING,
              },
              {
                path: rootPages.settingsPath,
                element: <SettingsPage />,
                moduleSection: CarModuleSectionCode.SETTINGS,
              },
              {
                path: rootPages.notificationsPath,
                element: <NotificationsPage />,
                // moduleSection: CarModuleSectionCode.,
              },
              {
                path: rootPages.debug,
                element: <DebugPage />,
                hidden: !isDebug,
              },
              {
                path: rootPages.debugIcons,
                element: <DebugIconsPage />,
                hidden: !isDebug,
              },
              {
                path: rootPages.debugPdf,
                element: <DebugPdfPage />,
                hidden: !isDebug,
              },
            ]}
            defaultPath={rootPages.home}
          />
        </ScrollContextProvider>
      </SimpleBarStyled>
    </>
  );
};
